import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Undo2, FileDown } from "lucide-react";

const STATUS_BADGE = {
  completed: "bg-primary/10 text-primary",
  processing: "bg-amber-100 text-amber-800",
  rolled_back: "bg-muted text-muted-foreground",
};

export default function ImportHistoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [batches, setBatches] = useState(null);

  const load = useCallback(() => {
    api.get("/imports").then((r) => setBatches(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const rollback = async (b) => {
    try {
      const { data } = await api.post(`/imports/${b.id}/rollback`);
      toast.success(`Import undone. ${data.orders_removed} orders and ${data.customers_removed} customers were removed.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadErrors = async (b) => {
    const res = await api.get(`/imports/${b.id}/errors`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-errors-${b.filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="import-history-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Import History</h1>
        <p className="text-muted-foreground mt-2">Every file that has been imported. An administrator can undo any import completely.</p>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="imports-table">
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead className="hidden md:table-cell">Uploaded</TableHead>
                <TableHead className="text-right">Orders added</TableHead>
                <TableHead className="text-right hidden sm:table-cell">New customers</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Duplicates skipped</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches === null ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : batches.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="no-batches-message">No imports yet.</TableCell></TableRow>
              ) : (
                batches.map((b) => (
                  <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                    <TableCell className="py-4">
                      <p className="font-medium">{b.filename}</p>
                      <p className="text-xs text-muted-foreground">{b.source_platform} · by {b.uploaded_by_email}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDate(b.uploaded_at)}</TableCell>
                    <TableCell className="text-right">{b.orders_added}</TableCell>
                    <TableCell className="text-right hidden sm:table-cell">{b.new_customers}</TableCell>
                    <TableCell className="text-right hidden sm:table-cell">{b.duplicates_skipped}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_BADGE[b.status] || ""}`}>
                        {b.status === "rolled_back" ? "Undone" : b.status === "processing" ? "Running" : "Completed"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {b.rows_failed > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => downloadErrors(b)} title="Download error report" data-testid={`errors-btn-${b.id}`}>
                            <FileDown className="h-4 w-4" />
                          </Button>
                        )}
                        {isAdmin && b.status === "completed" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`rollback-btn-${b.id}`}>
                                <Undo2 className="h-4 w-4 mr-1" /> Undo
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-serif">Undo this entire import?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  All {b.orders_added} orders from "{b.filename}" will be removed, and the {b.new_customers} customers it created will be removed too (unless they have other orders). The database goes back to how it was before this import.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep the import</AlertDialogCancel>
                                <AlertDialogAction onClick={() => rollback(b)} className="bg-destructive hover:bg-destructive/90" data-testid={`confirm-rollback-${b.id}`}>
                                  Yes, undo it
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
